/**
 * Declara un módulo.
 *
 * Existe para que `reasonLabels` tenga que cubrir exactamente `reasons`: el parámetro
 * de tipo se infiere de las razones que escribas, así que olvidar una etiqueta —o
 * escribir una de más, con un typo— **no compila**. La alternativa es un historial que
 * le muestra `wage_bonus` a un estudiante de primer año.
 */
export function economyModule(def) {
    return def;
}
export const ECONOMY_MODULES = {
    /**
     * Las partidas de minijuego. Es la implementación de referencia para cualquier
     * fuente de ingreso: reclamación atómica de la sesión, techo por ventana,
     * plausibilidad del puntaje y una referencia única por partida.
     *
     * Su código es anterior a este catálogo y sigue en `economy/gameSessions.ts` con su
     * router en `api/games.ts`. Ver la nota de `economy/modules/index.ts`.
     */
    games: economyModule({
        id: "games",
        name: "Minijuegos",
        kind: "earn",
        reasons: ["game_reward"],
        reasonsRequiringRef: ["game_reward"],
        reasonLabels: { game_reward: "Premio de juego" },
    }),
    /**
     * La tienda de ítems. Referencia para cualquier sumidero: cobrar y entregar en una
     * sola operación, y traducir el choque del índice único a "ya lo tienes" en vez de
     * a un error interno.
     *
     * Su código está en `economy/shop.ts` con su router en `api/shop.ts`, también
     * anterior al catálogo.
     */
    shop: economyModule({
        id: "shop",
        name: "Tienda",
        kind: "spend",
        reasons: ["purchase"],
        reasonsRequiringRef: ["purchase"],
        reasonLabels: { purchase: "Compra" },
    }),
    /**
     * El volley del coliseo: paga al que gana el partido.
     *
     * Es el primer módulo escrito CONTRA este registro, y por eso vive entero en
     * `economy/modules/volley/` en vez de estar cableado a mano como `games` y `shop`.
     *
     * Se diferencia de `games` en algo que no se ve desde acá y ordena todo su diseño:
     * **el puntaje no lo reporta nadie.** El servidor arbitra el partido, así que no hay
     * cifra del cliente que validar y sobran los cinco límites de plausibilidad de
     * `GameDef`. Lo que sí hace falta —y `games` no necesitaba— es un techo contra dos
     * cuentas turnándose las victorias: ver `VOLLEY_COIN_CAP`.
     */
    /**
     * Los muebles del iglú: el segundo sumidero del juego, después de la tienda.
     *
     * Tiene razón propia (`furniture_purchase`) en vez de reusar `purchase` porque el día
     * que haya que responder "¿en qué se le fueron las monedas a este jugador?" la
     * respuesta tiene que distinguir una gorra de un sofá — y eso sale de una consulta
     * sobre `reason`, no de adivinar por la `ref`.
     *
     * Colocar y quitar NO mueven monedas: lo comprado se queda comprado, y reordenar el
     * cuarto no puede costar ni devolver nada. Por eso el módulo escribe una sola razón.
     */
    igloo: economyModule({
        id: "igloo",
        name: "Iglú",
        kind: "spend",
        reasons: ["furniture_purchase"],
        reasonsRequiringRef: ["furniture_purchase"],
        reasonLabels: { furniture_purchase: "Mueble del iglú" },
    }),
    /**
     * La caja común de un club: monedas que dejan de ser de nadie.
     *
     * Es el primer módulo `vault` de verdad, y elige la variante que `docs/ECONOMY.md`
     * recomienda: no guarda un segundo saldo del jugador, se lo GASTA. Aportar es un
     * movimiento negativo real, así que `reconcile()` le sigue cuadrando a la cuenta; lo
     * que el club tiene es crédito suyo, que vive en la tabla del módulo y no en el libro.
     *
     * Comprar con esa caja **no** escribe en el libro: esas monedas ya salieron de la
     * economía al aportarse, y anotarlas otra vez las contaría dos veces.
     *
     * Sin retiro, a propósito: es lo que hace que aportar sea irreversible y que nadie
     * —tampoco el presidente— pueda vaciar lo que pusieron los demás.
     */
    club: economyModule({
        id: "club",
        name: "Salas de club",
        kind: "vault",
        reasons: ["club_deposit"],
        reasonsRequiringRef: ["club_deposit"],
        reasonLabels: { club_deposit: "Aporte a la caja del club" },
    }),
    volley: economyModule({
        id: "volley",
        name: "Volley del coliseo",
        kind: "earn",
        reasons: ["volley_win"],
        reasonsRequiringRef: ["volley_win"],
        reasonLabels: { volley_win: "Victoria en el volley" },
    }),
};
export const ECONOMY_MODULE_IDS = Object.keys(ECONOMY_MODULES);
export const ECONOMY_MODULE_LIST = ECONOMY_MODULE_IDS.map((id) => ECONOMY_MODULES[id]);
export function isEconomyModuleId(value) {
    return typeof value === "string" && Object.hasOwn(ECONOMY_MODULES, value);
}
export function getEconomyModule(id) {
    return isEconomyModuleId(id) ? ECONOMY_MODULES[id] : undefined;
}
/** Los módulos de un tipo. Para reportes: por dónde entran o salen las monedas. */
export function economyModulesOfKind(kind) {
    return ECONOMY_MODULE_LIST.filter((module) => module.kind === kind);
}
//# sourceMappingURL=modules.js.map